import React, { createContext, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const TaskContext = createContext();

export const useTask = () => {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error('useTask must be used within a TaskProvider');
  }
  return context;
};

export const TaskProvider = ({ children }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ status: '', priority: '', search: '' });

  // Get all tasks
  const getTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.priority) params.append('priority', filter.priority);
      if (filter.search) params.append('search', filter.search);

      const res = await axios.get(`/api/tasks?${params.toString()}`);
      setTasks(res.data.data);
    } catch (error) {
      // Don't show error if we're logging out or if it's a 401
      if (error.response?.status !== 401) {
        toast.error(error.response?.data?.message || 'Failed to fetch tasks');
      }
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // Create task
  const createTask = async (taskData) => {
    try {
      const res = await axios.post('/api/tasks', taskData);
      setTasks([res.data.data, ...tasks]);
      toast.success('Task created successfully!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create task');
      return false;
    }
  };

  // Update task
  const updateTask = async (id, taskData) => {
    try {
      const res = await axios.put(`/api/tasks/${id}`, taskData);
      setTasks(tasks.map(task => (task._id === id ? res.data.data : task)));
      toast.success('Task updated successfully!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update task');
      return false;
    }
  };

  // Delete task
  const deleteTask = async (id) => {
    try {
      await axios.delete(`/api/tasks/${id}`);
      setTasks(tasks.filter(task => task._id !== id));
      toast.success('Task deleted successfully!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete task');
      return false;
    }
  };

  // Update filter
  const updateFilter = (newFilter) => {
    setFilter({ ...filter, ...newFilter });
  };

  // Clear filter
  const clearFilter = () => {
    setFilter({ status: '', priority: '', search: '' });
  };

  const value = {
    tasks,
    loading,
    filter,
    getTasks,
    createTask,
    updateTask,
    deleteTask,
    updateFilter,
    clearFilter
  };

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
};

export default TaskContext;
